import type React from 'react'
import {clsx} from 'clsx'
import styles from './LoadingSkeleton.module.css'
import {useStableRandom} from '@github-ui/use-stable-random'

const PSEUDO_RANDOM_WIDTHS = [60, 40, 54, 77, 57, 41, 75, 45, 76, 55]

type SizePreset = 'xl' | 'lg' | 'md' | 'sm'

const WIDTH_CLASSES: Record<SizePreset, string> = {
  xl: styles.widthXl,
  lg: styles.widthLg,
  md: styles.widthMd,
  sm: styles.widthSm,
}

const HEIGHT_CLASSES: Record<SizePreset, string> = {
  xl: styles.heightXl,
  lg: styles.heightLg,
  md: styles.heightMd,
  sm: styles.heightSm,
}

export type LoadingSkeletonProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'width' | 'height'> & {
  animationStyle?: 'wave' | 'pulse'
  as?: React.ElementType
  height?: SizePreset | (string & {})
  variant?: 'rectangular' | 'elliptical' | 'rounded' | 'pill' | 'text'
  width?: 'random' | SizePreset | (string & {})
}

function isSizePreset(value: string): value is SizePreset {
  return value in WIDTH_CLASSES
}

export function LoadingSkeleton({
  animationStyle = 'wave',
  as: Component = 'div',
  height,
  variant = 'rectangular',
  width,
  className,
  ...rest
}: LoadingSkeletonProps) {
  const index = useStableRandom({max: PSEUDO_RANDOM_WIDTHS.length})

  const widthClass = width && isSizePreset(width) ? WIDTH_CLASSES[width] : undefined
  const heightClass = height && isSizePreset(height) ? HEIGHT_CLASSES[height] : undefined

  const inlineStyles: React.CSSProperties = {}

  if (width === 'random') {
    inlineStyles.width = `${PSEUDO_RANDOM_WIDTHS[index]}%`
  } else if (width && !widthClass) {
    inlineStyles.width = width
  }

  if (height && !heightClass) {
    inlineStyles.height = height
  }

  return (
    <Component
      className={clsx(
        styles.skeleton,
        animationStyle === 'pulse' && styles.pulse,
        styles[variant],
        widthClass,
        heightClass,
        className,
      )}
      style={inlineStyles}
      {...rest}
    />
  )
}
